import { EnsureAugloopRuntimeManager, GetAugloopEventEmitter, waitForAugloopConnectionToBeConnected } from '../augloop/Augloop';
import { IAugLoopRuntimeManager } from '@augloop/office-online';
import { AugloopEvent } from '../interfaces/IAugloopEventEmitter';
import { LogEvents } from '../../utils/LogEvents';
import { LogEventNames } from '../interfaces/ITelemetryLogger';
import { LogError } from '../../utils/ErrorTelemetry';
import { ErrorStringFromException } from '../../utils/utils';
import { AugloopKeys } from '../augloop/telemetry/AugloopKeys';
import { LogSingleEvent } from '../TelemetryLogger/LogEvent';
import { RetryServiceCallUntilSuccessOrTimeout, RetryServiceCaller } from '../utils/Utils';
import { LogServiceEvent } from '../TelemetryLogger/LogServiceEvent';
import { IAnnotationObserver, initializeCopilotLicensingClient, CopilotLicensingClientRequestPoller } from '@1js/al-voice-client';
import { FILE_UPLOAD_POLLING_DELAY_MILLISECONDS } from '../../AppConstants';
import { IndexeddbCacheService } from '../cache/IndexeddbCacheService';
import { ProvisionAuthenticationToken } from '../office-js/auth/IdentityUtil';
import { IAuthenticationProvider } from '../interfaces/IAuthenticationProvider';

export class CopilotLicensingTask {
    private alRuntimeManager: IAugLoopRuntimeManager | null = null;
    private copilotLicensingPoller: CopilotLicensingClientRequestPoller | null = null;
    private removeLicensingEventHandlers: null | (() => void) = null;
    private copilotLicensingInitRetryObj: any = null;
    private annotationObserver: IAnnotationObserver | undefined;
    private licenseCache: IndexeddbCacheService<boolean>;
    private readonly CACHE_KEY = 'copilot_license';

    constructor() {
        LogEvents.Boot.Start();
        this.annotationObserver = {
            onAnnotationResult: (op) => {
                if (this.copilotLicensingPoller) {
                    this.copilotLicensingPoller.onAnnotationResult(op);
                }
            },
        };
        this.licenseCache = new IndexeddbCacheService<boolean>(
            'copilotLicenseDB',
            'copilotLicenses',
            60 * 60 * 1000 // set cache expiry to 1 hour
        );
    }

    private async initializeCopilotLicensingClient() {
        if (this.alRuntimeManager == null) {
            throw new Error("alRuntime manager is not initialized");
        }

        const augloopSession = await this.alRuntimeManager.getSession();
        LogSingleEvent<AugloopKeys>(LogEventNames.Augloop, [
            { key: AugloopKeys.IsConnectedToAL, value: augloopSession.isConnected },
            { key: AugloopKeys.HasConnectedToAL, value: augloopSession.hasConnected },
            { key: AugloopKeys.ConnectionClosedWithAL, value: augloopSession.isClosed }
        ]);

        const logServiceEventInitialize = new LogServiceEvent(LogEventNames.ServiceCopilotLicensingInitialize);
        logServiceEventInitialize.Start();

        this.copilotLicensingInitRetryObj = RetryServiceCallUntilSuccessOrTimeout<void>(logServiceEventInitialize, () => initializeCopilotLicensingClient(this.alRuntimeManager! as any /* TODO: ADO:4691502 remove cast */, this.annotationObserver, true /*enable set annotations from voice client */), false /*shouldRetryOnNull*/, RetryServiceCaller.InitalizeCopilotLicensingClient);
    }

    private async ensureCopilotLicensingClientInitialized(): Promise<void> {
        if (this.copilotLicensingInitRetryObj != null) {
            return this.copilotLicensingInitRetryObj.promise;
        }

        const onSessionConnectAfterCloseListener = () => {
            this.initializeCopilotLicensingClient();
        }

        const onSessionCloseListener = () => {
            GetAugloopEventEmitter().once(AugloopEvent.SessionConnect, onSessionConnectAfterCloseListener);
        }

        // If the augloop session is closed and we reconnect, re-initialize copilot licensing client.
        GetAugloopEventEmitter().on(AugloopEvent.SessionClose, onSessionCloseListener);

        this.removeLicensingEventHandlers = () => {
            GetAugloopEventEmitter().removeListener(AugloopEvent.SessionClose, onSessionCloseListener);
            GetAugloopEventEmitter().removeListener(AugloopEvent.SessionConnect, onSessionConnectAfterCloseListener);
            this.removeLicensingEventHandlers = null;
        }

        await this.initializeCopilotLicensingClient();

        if (this.copilotLicensingInitRetryObj == null) {
            throw new Error("Failed creating copilot licensing client");
        }

        return this.copilotLicensingInitRetryObj.promise;
    }

    public async getCachedResult(): Promise<boolean | null> {
        await this.licenseCache.initialize();
        return await this.licenseCache.get(this.CACHE_KEY);
    }

    public async checkCopilotLicense(authProvider: IAuthenticationProvider): Promise<boolean> {
        try {
            // check cache
            const cachedResult = await this.getCachedResult();
            if (cachedResult !== null) {
                return cachedResult;
            }

            const isTokenProvisioned = await ProvisionAuthenticationToken(authProvider);
            if (!isTokenProvisioned) {
                // If the token is not provisioned, app will hang when checking copilot license
                return false;
            }

            this.alRuntimeManager = await EnsureAugloopRuntimeManager();
            await waitForAugloopConnectionToBeConnected();
            await this.ensureCopilotLicensingClientInitialized();

            if (!this.copilotLicensingPoller) {
                this.copilotLicensingPoller = new CopilotLicensingClientRequestPoller(
                    FILE_UPLOAD_POLLING_DELAY_MILLISECONDS,
                    this.alRuntimeManager! as any,
                );
            }

            const result = await this.copilotLicensingPoller.makeRequest();
            const isCopilotLicenseEnabled = result?.copilotLicensingInfo?.copilotLicenseAvailable === true;

            // cache the result
            await this.licenseCache.set(this.CACHE_KEY, isCopilotLicenseEnabled);

            return isCopilotLicenseEnabled;
        } catch (error) {
            LogError(LogEventNames.Boot, "failed to check copilot license: " + ErrorStringFromException(error));
            return false;
        } finally {
            if (this.removeLicensingEventHandlers) {
                this.removeLicensingEventHandlers();
            }
            if (this.copilotLicensingPoller) {
                this.copilotLicensingPoller = null;
            }
            if (this.copilotLicensingInitRetryObj) {
                this.copilotLicensingInitRetryObj = null;
            }
            this.licenseCache.close();
        }
    }
}